import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '@/shared/ui';
import { useThemeLogo } from '@/styles/themes';
import type { AnnouncementType } from '@/shared/api/announcements';
import { useActiveAnnouncements, useDismissAnnouncement, useMarkAnnouncementSeen } from './useAnnouncements';
import styles from './AnnouncementModal.module.css';

const TYPE_ICON: Record<AnnouncementType, string> = {
  info: 'fa-circle-info',
  success: 'fa-circle-check',
  warning: 'fa-triangle-exclamation',
  critical: 'fa-circle-exclamation',
};

/**
 * Shows modal-style announcements one at a time after login (typically `critical`
 * severity). A branded, full-bleed dialog with the Casalary logo and a severity
 * badge — for bigger announcements that deserve more than a banner. Acknowledging a
 * dismissible one persists the dismissal; a non-dismissible one closes for the session.
 */
export function AnnouncementModal() {
  const { t } = useTranslation();
  const logoSrc = useThemeLogo();
  const { data } = useActiveAnnouncements();
  const dismiss = useDismissAnnouncement();
  const markSeen = useMarkAnnouncementSeen();
  const [closedIds, setClosedIds] = useState<string[]>([]);

  const modals = (data ?? []).filter(
    (a) => (a.display === 'modal' || a.display === 'both') && !closedIds.includes(a._id)
  );
  const current = modals[0];
  const currentId = current?._id;

  useEffect(() => {
    if (currentId) markSeen.mutate(currentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  if (!current) return null;

  const close = () => {
    if (current.dismissible) dismiss.mutate(current._id);
    setClosedIds((ids) => [...ids, current._id]);
  };

  return (
    <Modal
      open
      onClose={close}
      size="md"
      footer={
        <Button fullWidth onClick={close}>
          {t('announcement.gotIt')}
        </Button>
      }
    >
      <div className={styles.wrap}>
        <div className={styles.hero} data-type={current.type}>
          <img src={logoSrc} alt="Casalary" className={styles.logo} />
          <span className={styles.badge} data-type={current.type}>
            <i className={`fa-solid ${TYPE_ICON[current.type]}`} aria-hidden="true" />
          </span>
        </div>
        <div className={styles.content}>
          <h2 className={styles.title}>{current.title}</h2>
          <p className={styles.body}>{current.body}</p>
        </div>
      </div>
    </Modal>
  );
}
